import React, { useCallback, useEffect, useState } from "react";
import { Pressable, StyleSheet, TextInput, View, useWindowDimensions } from "react-native";
import { CaretLeftIcon, CaretRightIcon, CheckIcon, PencilSimpleIcon, XIcon } from "@phosphor-icons/react";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import type { SegmentFieldConfig, SegmentVariant, StructuredFields } from "../types/reviewTypes";

// ---------------------------------------------------------------------------
// Segment configurations per variant
// ---------------------------------------------------------------------------

const ACTIVITY_SEGMENTS: SegmentFieldConfig[] = [
  { key: "title", label: "TITLE", inputType: "text" },
  { key: "description", label: "DESCRIPTION", inputType: "textarea" },
  { key: "dueDate", label: "DUE DATE", inputType: "date" },
  { key: "points", label: "POINTS POSSIBLE", inputType: "number" },
];

const PAGE_SEGMENTS: SegmentFieldConfig[] = [
  { key: "title", label: "TITLE", inputType: "text" },
  { key: "description", label: "PAGE CONTENT", inputType: "textarea" },
];

const LINK_SEGMENTS: SegmentFieldConfig[] = [
  { key: "title", label: "TITLE", inputType: "text" },
  { key: "url", label: "URL", inputType: "url" },
];

const CHECKLIST_SEGMENTS: SegmentFieldConfig[] = [
  { key: "title", label: "TITLE", inputType: "text" },
  { key: "description", label: "DESCRIPTION", inputType: "textarea" },
];

const RECOMMENDATION_SEGMENTS: SegmentFieldConfig[] = [
  { key: "title", label: "TITLE", inputType: "text" },
  { key: "description", label: "DESCRIPTION", inputType: "textarea" },
];

const MODULE_SEGMENTS: SegmentFieldConfig[] = [
  { key: "title", label: "NAME", inputType: "text" },
  { key: "description", label: "DESCRIPTION", inputType: "textarea" },
];

const VARIANT_SEGMENTS: Record<SegmentVariant, SegmentFieldConfig[]> = {
  activity: ACTIVITY_SEGMENTS,
  page: PAGE_SEGMENTS,
  link: LINK_SEGMENTS,
  checklist: CHECKLIST_SEGMENTS,
  recommendation: RECOMMENDATION_SEGMENTS,
  module: MODULE_SEGMENTS,
};

function parsePointsInput(value: string): number | null | undefined {
  const trimmedValue = value.trim();
  if (!trimmedValue) return undefined;

  const parsedValue = Number(trimmedValue);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SegmentedDiffViewProps {
  variant: SegmentVariant;
  currentFields: StructuredFields;
  proposedFields: StructuredFields;
  onSaveEdit?: (editedFields: StructuredFields) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function SegmentedDiffView({ variant, currentFields, proposedFields, onSaveEdit }: SegmentedDiffViewProps) {
  const { width } = useWindowDimensions();
  const showTwoColumns = width >= 900;
  const allSegments = VARIANT_SEGMENTS[variant];
  const {
    title: proposedTitle,
    description: proposedDescription,
    dueDate: proposedDueDate,
    points: proposedPoints,
    url: proposedUrl,
  } = proposedFields;

  // Filter out segments that have no data on either side (e.g., dueDate when not set)
  const segments = allSegments.filter((seg) => {
    const currentVal = currentFields[seg.key];
    const proposedVal = proposedFields[seg.key];
    return currentVal != null || proposedVal != null;
  });

  const [currentCollapsed, setCurrentCollapsed] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingFields, setEditingFields] = useState<StructuredFields>({ ...proposedFields });

  // Reset editing state when proposed fields change (item switch)
  useEffect(() => {
    setIsEditing(false);
    setCurrentCollapsed(false);
    setEditingFields({
      title: proposedTitle,
      description: proposedDescription,
      dueDate: proposedDueDate,
      points: proposedPoints,
      url: proposedUrl,
    });
  }, [proposedTitle, proposedDescription, proposedDueDate, proposedPoints, proposedUrl]);

  // Auto-collapse current column when editing starts
  useEffect(() => {
    setCurrentCollapsed(isEditing);
  }, [isEditing]);

  const handleEdit = useCallback(() => {
    setEditingFields({ ...proposedFields });
    setIsEditing(true);
  }, [proposedFields]);

  const handleSaveEdit = useCallback(() => {
    setIsEditing(false);
    onSaveEdit?.(editingFields);
  }, [editingFields, onSaveEdit]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditingFields({ ...proposedFields });
  }, [proposedFields]);

  const handleFieldChange = useCallback((key: keyof StructuredFields, value: string) => {
    setEditingFields((prev) => {
      if (key !== "points") {
        return {
          ...prev,
          [key]: value,
        };
      }

      const parsedPoints = parsePointsInput(value);
      if (parsedPoints === null) {
        return prev;
      }

      return {
        ...prev,
        points: parsedPoints,
      };
    });
  }, []);

  return (
    <View style={styles.container}>
      {/* Headers row */}
      <View
        style={[
          styles.headersRow,
          showTwoColumns && !currentCollapsed ? styles.headersRowDesktop : styles.headersRowMobile,
        ]}>
        {!currentCollapsed && (
          <View style={[styles.currentHeader, showTwoColumns && styles.headerHalf]}>
            <CustomText
              text="Current"
              useTranslationText={false}
              size="s"
              weight="ultraBold"
              style={styles.currentHeaderText}
            />
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Collapse current"
              onPress={() => setCurrentCollapsed(true)}
              style={styles.iconButton}>
              <CaretLeftIcon size={14} color={colors.grey[500]} weight="bold" />
            </Pressable>
          </View>
        )}
        <View style={[styles.proposedHeader, !currentCollapsed && showTwoColumns && styles.headerHalf]}>
          <View style={styles.proposedHeaderLeft}>
            {currentCollapsed && (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Expand current"
                onPress={() => setCurrentCollapsed(false)}
                style={styles.iconButtonSuccess}>
                <CaretRightIcon size={14} color={colors.success[700]} weight="bold" />
              </Pressable>
            )}
            <CustomText
              text="Proposed"
              useTranslationText={false}
              size="s"
              weight="ultraBold"
              style={styles.proposedHeaderText}
            />
          </View>
          {isEditing ? (
            <View style={styles.editingActions}>
              <Pressable onPress={handleCancelEdit} style={styles.cancelButton}>
                <XIcon size={12} color={colors.grey[600]} weight="bold" />
                <CustomText text="Cancel" useTranslationText={false} size="xs" style={styles.cancelButtonText} />
              </Pressable>
              <Pressable onPress={handleSaveEdit} style={styles.saveButton}>
                <CheckIcon size={12} color={colors.white} weight="bold" />
                <CustomText text="Save" useTranslationText={false} size="xs" style={styles.saveButtonText} />
              </Pressable>
            </View>
          ) : (
            <Pressable onPress={handleEdit} style={styles.editButton}>
              <PencilSimpleIcon size={14} color={colors.grey[700]} weight="regular" />
              <CustomText text="Edit" useTranslationText={false} size="xs" style={styles.editButtonText} />
            </Pressable>
          )}
        </View>
      </View>

      {/* Segment rows */}
      {segments.map((seg, index) => {
        const proposedValue = (isEditing ? editingFields : proposedFields)[seg.key];
        const currentValue = currentFields[seg.key];
        const proposedDisplay = proposedValue != null ? String(proposedValue) : "";
        const currentDisplay = currentValue != null ? String(currentValue) : "";
        const hasChanged = currentDisplay !== proposedDisplay;
        const isLast = index === segments.length - 1;

        return (
          <View
            key={seg.key}
            style={[
              styles.segmentRow,
              showTwoColumns && !currentCollapsed ? styles.segmentRowDesktop : styles.segmentRowMobile,
              !isLast && styles.segmentRowBorder,
            ]}>
            {/* Current field */}
            {!currentCollapsed && (
              <View
                style={[
                  styles.fieldBlock,
                  showTwoColumns && styles.fieldBlockHalf,
                  showTwoColumns && styles.fieldBlockCurrentBorder,
                ]}>
                <View style={styles.fieldLabelRow}>
                  <CustomText
                    text={seg.label}
                    useTranslationText={false}
                    size="xs"
                    weight="bold"
                    style={styles.fieldLabel}
                  />
                </View>
                <CustomText
                  text={currentDisplay || "Not set"}
                  useTranslationText={false}
                  size="s"
                  style={currentDisplay ? styles.fieldValue : styles.fieldValueEmpty}
                />
              </View>
            )}

            {/* Proposed field */}
            <View
              style={[
                styles.fieldBlock,
                !currentCollapsed && showTwoColumns && styles.fieldBlockHalf,
                hasChanged && !isEditing && styles.fieldBlockChanged,
              ]}>
              <View style={styles.fieldLabelRow}>
                <CustomText
                  text={seg.label}
                  useTranslationText={false}
                  size="xs"
                  weight="bold"
                  style={styles.proposedFieldLabel}
                />
                {!isEditing && hasChanged && (
                  <View style={styles.changedBadge}>
                    <CustomText
                      text="Changed"
                      useTranslationText={false}
                      size="xs"
                      weight="medium"
                      style={styles.changedBadgeText}
                    />
                  </View>
                )}
                {!isEditing && !hasChanged && currentDisplay !== "" && (
                  <CustomText text="No change" useTranslationText={false} size="xs" style={styles.noChangeBadgeText} />
                )}
              </View>
              {isEditing ? (
                <FieldInput config={seg} value={proposedDisplay} onChange={(v) => handleFieldChange(seg.key, v)} />
              ) : (
                <CustomText
                  text={proposedDisplay || "Not set"}
                  useTranslationText={false}
                  size="s"
                  style={proposedDisplay ? styles.fieldValue : styles.fieldValueEmpty}
                />
              )}
            </View>
          </View>
        );
      })}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Field Input
// ---------------------------------------------------------------------------

interface FieldInputProps {
  config: SegmentFieldConfig;
  value: string;
  onChange: (value: string) => void;
}

function FieldInput({ config, value, onChange }: FieldInputProps) {
  const isMultiline = config.inputType === "textarea";
  const isNumber = config.inputType === "number";

  return (
    <TextInput
      value={value}
      onChangeText={onChange}
      multiline={isMultiline}
      keyboardType={isNumber ? "numeric" : config.inputType === "url" ? "url" : "default"}
      style={[styles.textInput, isMultiline && styles.textInputMultiline, isNumber && styles.textInputNumber]}
      textAlignVertical={isMultiline ? "top" : "center"}
      placeholder={`Enter ${config.label.toLowerCase()}`}
      placeholderTextColor={colors.grey[500]}
    />
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    overflow: "hidden",
  },
  headersRow: {},
  headersRowDesktop: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  headersRowMobile: {
    flexDirection: "column",
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  headerHalf: {
    flex: 1,
  },
  currentHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    height: 40,
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
  },
  currentHeaderText: {
    color: colors.grey[700],
  },
  proposedHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    height: 40,
    backgroundColor: "#F3FEE7",
  },
  proposedHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  proposedHeaderText: {
    color: colors.success[700],
  },
  iconButton: {
    padding: 4,
    borderRadius: 4,
  },
  iconButtonSuccess: {
    padding: 4,
    borderRadius: 4,
  },
  editButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
  },
  editButtonText: {
    color: colors.grey[700],
  },
  editingActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  cancelButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  cancelButtonText: {
    color: colors.grey[600],
  },
  saveButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: colors.brand[600],
  },
  saveButtonText: {
    color: colors.white,
  },
  // --- Segment rows ---
  segmentRow: {},
  segmentRowDesktop: {
    flexDirection: "row",
  },
  segmentRowMobile: {
    flexDirection: "column",
  },
  segmentRowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  fieldBlock: {
    flex: 1,
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  fieldBlockHalf: {
    flex: 1,
  },
  fieldBlockCurrentBorder: {
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
  },
  fieldBlockChanged: {
    backgroundColor: "rgba(240, 253, 244, 0.3)",
  },
  fieldLabelRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
  },
  fieldLabel: {
    color: colors.grey[400],
    fontSize: 11,
    letterSpacing: 0.5,
  },
  proposedFieldLabel: {
    color: colors.grey[400],
    fontSize: 11,
    letterSpacing: 0.5,
  },
  changedBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: "#F3FEE7",
  },
  changedBadgeText: {
    color: "#3B7C0F",
    fontSize: 10,
  },
  noChangeBadgeText: {
    color: colors.grey[500],
    fontSize: 10,
  },
  fieldValue: {
    color: colors.grey[900],
    lineHeight: 22,
  },
  fieldValueEmpty: {
    color: colors.grey[400],
    lineHeight: 22,
    fontStyle: "italic",
  },
  textInput: {
    padding: 10,
    fontSize: 14,
    lineHeight: 22,
    color: colors.grey[900],
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: 8,
    fontFamily: "Inter",
  },
  textInputMultiline: {
    minHeight: 200,
    textAlignVertical: "top",
  },
  textInputNumber: {
    width: 128,
  },
});

export default SegmentedDiffView;
